// Pure timeline ops for a video Composition (N-series): BEAT management (the art-direction
// spine: backgrounds, VO, duration, transitions) + FREE LAYER CLIPS (every visual layer as a
// deck-level clip on absolute EFFECTIVE reel time — see video-time.ts). Layer CONTENT editing
// reuses composition-ops through the provider's synthesized active-beat view; these ops own
// WHERE things sit in time. Never mutate the input; every op flips video.meta.dirty so
// autosave persists it (and an explicit Render re-composites the reel).
//
// Beats vs clips when TIME changes (trim/reorder/delete/duplicate): a clip FULLY CONTAINED in
// one beat is treated as that beat's content — it travels with the beat on reorder, stays
// glued to it when earlier beats retime, and dies with it on delete. A clip SPANNING beats
// (watermark, persistent CTA) is free: it keeps its absolute window, only clamped to the new
// reel length. This keeps "beats as art direction" and "layers as free pieces" coherent.

import type {
  BeatBackground,
  VideoBeatComposition,
  VideoComposition,
  VideoLayerClip,
} from '../types'
import { MIN_CLIP_SEC, videoBeatSpans, videoTotalDuration, type BeatSpan } from '../video-time'
import { clone, newLayerId } from './composition-ops'

// Reel beat bounds (mirror MIN_BEATS/MAX_BEATS in the worker video-types).
export const MIN_BEATS = 3
export const MAX_BEATS = 6
// Per-beat duration bounds (seconds). A beat too short can't carry its VO; too long overstays.
export const MIN_BEAT_SECONDS = 1.5
export const MAX_BEAT_SECONDS = 15

const round1 = (t: number) => Math.round(t * 10) / 10
// Clip windows keep centisecond precision: snap targets (crossfaded beat starts, other clips'
// edges) are NOT on the 0.1s grid, and rounding a snapped value would un-snap it on release.
const round2 = (t: number) => Math.round(t * 100) / 100

let SEQ = 0
export function newBeatId(): string {
  SEQ = (SEQ + 1) % 1_000_000
  return `vb_${Date.now().toString(36)}_${SEQ.toString(36)}`
}

/** The clips array (normalize guarantees it exists; tolerate a missing one anyway). */
export function clipsOf(video: VideoComposition): VideoLayerClip[] {
  return video.layers ?? []
}

function withClips(video: VideoComposition, layers: VideoLayerClip[]): VideoComposition {
  return { ...video, layers, meta: { ...video.meta, dirty: true } }
}

/** The index of the beat that fully CONTAINS the clip, or -1 for a cross-beat/free clip.
 *  50ms tolerance: sub-frame drift (float noise, or clips normalized by an older spans
 *  version) must never demote a beat's own text to a "free" clip. */
export function clipOwnerIndex(spans: BeatSpan[], clip: VideoLayerClip): number {
  for (let i = 0; i < spans.length; i++) {
    if (clip.start >= spans[i]!.start - 0.05 && clip.end <= spans[i]!.end + 0.05) return i
  }
  return -1
}

/** Re-anchor clips after a beat-time change: contained clips follow their beat (by id, same
 *  offset from the beat's start, clamped into its new span; dropped if the beat is gone),
 *  cross-beat clips stay absolute (clamped to the new reel length). */
function retimeClips(video: VideoComposition, newBeats: VideoBeatComposition[]): VideoLayerClip[] {
  const oldSpans = videoBeatSpans(video.beats)
  const newSpans = videoBeatSpans(newBeats)
  const newTotal = newSpans.at(-1)?.end ?? 0
  const out: VideoLayerClip[] = []
  for (const clip of clipsOf(video)) {
    const own = clipOwnerIndex(oldSpans, clip)
    if (own >= 0) {
      const beatId = video.beats[own]!.id
      const j = newBeats.findIndex(b => b.id === beatId)
      if (j < 0) continue // its beat was deleted → the beat's content goes with it
      const span = newSpans[j]!
      const dur = span.end - span.start
      const offset = Math.min(clip.start - oldSpans[own]!.start, Math.max(0, dur - MIN_CLIP_SEC))
      const start = span.start + Math.max(0, offset)
      const end = Math.min(start + (clip.end - clip.start), span.end)
      out.push({ ...clip, start, end: Math.max(end, start + MIN_CLIP_SEC) })
    } else {
      if (clip.start >= newTotal - 1e-6) continue // fully past the new reel end
      const end = Math.min(clip.end, newTotal)
      out.push({
        ...clip,
        start: clip.start,
        end: Math.max(end, Math.min(clip.start + MIN_CLIP_SEC, newTotal)),
      })
    }
  }
  return out
}

/** Apply a new beat arrangement + re-anchor every clip to it. */
function withBeats(video: VideoComposition, beats: VideoBeatComposition[]): VideoComposition {
  return {
    ...video,
    beats,
    layers: retimeClips(video, beats),
    meta: { ...video.meta, dirty: true },
  }
}

/** A deep copy of a beat with a fresh stable id + role, marked user-added (userEdited). */
function freshBeat(
  src: VideoBeatComposition,
  role: VideoBeatComposition['role']
): VideoBeatComposition {
  const copy = clone(src)
  copy.id = newBeatId()
  copy.role = role
  copy.seeded = false
  copy.userEdited = true
  // A fresh beat's VO is new content the user will (re-)write → mark dirty so a render re-synths.
  copy.voDirty = true
  return copy
}

/** Duplicate beat `index` in place (inserted right after it), keeping its role. The clips
 *  CONTAINED in it are duplicated too (fresh layer ids), anchored to the copy at the same
 *  offsets — duplicating a storyboard beat brings its on-screen text along. */
export function duplicateBeat(video: VideoComposition, index: number): VideoComposition {
  const src = video.beats[index]
  if (!src || video.beats.length >= MAX_BEATS) return video
  const copy = freshBeat(src, src.role)
  const beats = [...video.beats.slice(0, index + 1), copy, ...video.beats.slice(index + 1)]
  const next = withBeats(video, beats)
  // Source-beat clips → copies anchored to the new beat's span (which withBeats just placed).
  const oldSpans = videoBeatSpans(video.beats)
  const newSpans = videoBeatSpans(beats)
  const srcSpan = oldSpans[index]!
  const copySpan = newSpans[index + 1]!
  const copies: VideoLayerClip[] = clipsOf(video)
    .filter(c => clipOwnerIndex(oldSpans, c) === index)
    .map(c => ({
      layer: { ...clone(c.layer), id: newLayerId(c.layer.type) },
      start: copySpan.start + (c.start - srcSpan.start),
      end: Math.min(copySpan.start + (c.end - srcSpan.start), copySpan.end),
    }))
  return copies.length ? withClips(next, [...clipsOf(next), ...copies]) : next
}

/** Append a new value beat (seeded from the last beat's look) to the end of the timeline. */
export function addBeat(video: VideoComposition): VideoComposition {
  if (video.beats.length >= MAX_BEATS) return video
  const template = video.beats[video.beats.length - 1] ?? video.beats[0]
  if (!template) return video
  return withBeats(video, [...video.beats, freshBeat(template, 'value')])
}

/** Remove beat `index` (guarded to keep the timeline at/above the minimum). Its contained
 *  clips are removed with it; later beats' clips shift up; cross-beat clips clamp. */
export function deleteBeat(video: VideoComposition, index: number): VideoComposition {
  if (video.beats.length <= MIN_BEATS) return video
  if (index < 0 || index >= video.beats.length) return video
  return withBeats(
    video,
    video.beats.filter((_, i) => i !== index)
  )
}

/** Move a beat from one position to another (drag-to-reorder). Contained clips travel with it. */
export function moveBeat(video: VideoComposition, from: number, to: number): VideoComposition {
  if (from === to || from < 0 || to < 0 || from >= video.beats.length || to >= video.beats.length)
    return video
  const beats = [...video.beats]
  const [moved] = beats.splice(from, 1)
  beats.splice(to, 0, moved!)
  return withBeats(video, beats)
}

/** Trim a beat's duration (clamped to the beat bounds). Contained clips clamp into the new
 *  span; every later beat's clips stay glued to their beats. */
export function trimBeat(
  video: VideoComposition,
  index: number,
  seconds: number
): VideoComposition {
  const beat = video.beats[index]
  if (!beat) return video
  const dur = Math.min(MAX_BEAT_SECONDS, Math.max(MIN_BEAT_SECONDS, round1(seconds)))
  if (dur === beat.durationSec) return video
  const beats = video.beats.map((b, i) =>
    i === index ? { ...b, durationSec: dur, userEdited: true } : b
  )
  return withBeats(video, beats)
}

/** Set a beat's spoken VO script; flips voDirty so the next explicit render re-synths its audio. */
export function setBeatVO(video: VideoComposition, index: number, vo: string): VideoComposition {
  const beat = video.beats[index]
  if (!beat || beat.vo === vo) return video
  const beats = video.beats.map((b, i) =>
    i === index ? { ...b, vo, voDirty: true, userEdited: true } : b
  )
  return { ...video, beats, meta: { ...video.meta, dirty: true } }
}

/** Toggle / swap the music bed (enabled flag + optional src), marking the timeline dirty. */
export function setMusic(
  video: VideoComposition,
  music: VideoComposition['music']
): VideoComposition {
  return { ...video, music, meta: { ...video.meta, dirty: true } }
}

/** Switch the reel's voice: every scripted beat flips voDirty (its cached take is in the OLD
 *  voice), so Play re-records per beat and the next render re-synths everything. */
export function setVoVoiceId(video: VideoComposition, voiceId: string): VideoComposition {
  if ((video.voVoiceId ?? '') === voiceId) return video
  const beats = video.beats.map(b => (b.vo.trim() ? { ...b, voDirty: true } : b))
  return { ...video, voVoiceId: voiceId, beats, meta: { ...video.meta, dirty: true } }
}

/** Set how a beat ENTERS from the previous one (cut/crossfade). No-op on the first beat.
 *  Changes the effective timeline (spans shift), so clips re-anchor like any time change. */
export function setBeatTransition(
  video: VideoComposition,
  index: number,
  transitionIn: 'cut' | 'crossfade'
): VideoComposition {
  const beat = video.beats[index]
  if (!beat || index === 0 || (beat.transitionIn ?? 'cut') === transitionIn) return video
  const beats = video.beats.map((b, i) =>
    i === index ? { ...b, transitionIn, userEdited: true } : b
  )
  return withBeats(video, beats)
}

/** Replace a beat's (always text-free) background — user-uploaded clip, brand photo, swapped clip. */
export function setBeatBackground(
  video: VideoComposition,
  index: number,
  background: BeatBackground
): VideoComposition {
  const beat = video.beats[index]
  if (!beat) return video
  const beats = video.beats.map((b, i) =>
    i === index ? { ...b, background, userEdited: true } : b
  )
  return { ...video, beats, meta: { ...video.meta, dirty: true } }
}

// ── Free layer clip ops (N-series) ──────────────────────────────────────────────────────────

function mapClip(
  video: VideoComposition,
  layerId: string,
  fn: (c: VideoLayerClip) => VideoLayerClip
): VideoComposition {
  const clips = clipsOf(video)
  const idx = clips.findIndex(c => c.layer.id === layerId)
  if (idx < 0) return video
  const next = fn(clips[idx]!)
  if (next === clips[idx]) return video
  return withClips(
    video,
    clips.map((c, i) => (i === idx ? next : c))
  )
}

/** Slide a clip to a new start (length preserved), clamped inside the reel. */
export function moveClip(
  video: VideoComposition,
  layerId: string,
  newStart: number
): VideoComposition {
  const total = videoTotalDuration(video.beats)
  return mapClip(video, layerId, c => {
    const len = c.end - c.start
    const start = round2(Math.max(0, Math.min(newStart, total - len)))
    if (Math.abs(start - c.start) < 0.005) return c
    return { ...c, layer: { ...c.layer, userEdited: true }, start, end: round2(start + len) }
  })
}

/** Drag one edge of a clip's window (trim/stretch), clamped to the reel + a minimum length. */
export function resizeClip(
  video: VideoComposition,
  layerId: string,
  edge: 'start' | 'end',
  t: number
): VideoComposition {
  const total = videoTotalDuration(video.beats)
  return mapClip(video, layerId, c => {
    if (edge === 'start') {
      const start = round2(Math.max(0, Math.min(t, c.end - MIN_CLIP_SEC)))
      if (Math.abs(start - c.start) < 0.005) return c
      return { ...c, layer: { ...c.layer, userEdited: true }, start }
    }
    const end = round2(Math.min(total, Math.max(t, c.start + MIN_CLIP_SEC)))
    if (Math.abs(end - c.end) < 0.005) return c
    return { ...c, layer: { ...c.layer, userEdited: true }, end }
  })
}

/** Set a clip's window outright (steppers / keyboard nudge). */
export function setClipWindow(
  video: VideoComposition,
  layerId: string,
  start: number,
  end: number
): VideoComposition {
  const total = videoTotalDuration(video.beats)
  return mapClip(video, layerId, c => {
    const s = round2(Math.max(0, Math.min(start, total - MIN_CLIP_SEC)))
    const e = round2(Math.min(total, Math.max(end, s + MIN_CLIP_SEC)))
    if (Math.abs(s - c.start) < 0.005 && Math.abs(e - c.end) < 0.005) return c
    return { ...c, layer: { ...c.layer, userEdited: true }, start: s, end: e }
  })
}

/** Set a clip's entrance/exit animation preset (null clears that edge). */
export function setClipAnim(
  video: VideoComposition,
  layerId: string,
  dir: 'in' | 'out',
  preset: 'fade' | 'slide' | 'pop' | null
): VideoComposition {
  return mapClip(video, layerId, c => {
    const anim = { ...(c.layer.anim ?? {}) }
    if (preset) anim[dir] = preset
    else delete anim[dir]
    return {
      ...c,
      layer: { ...c.layer, anim: anim.in || anim.out ? anim : undefined, userEdited: true },
    }
  })
}

/** Delete a clip from the timeline (and therefore from the reel). */
export function removeClip(video: VideoComposition, layerId: string): VideoComposition {
  const clips = clipsOf(video)
  if (!clips.some(c => c.layer.id === layerId)) return video
  return withClips(
    video,
    clips.filter(c => c.layer.id !== layerId)
  )
}

/** Duplicate a clip (fresh layer id, nudged +0.3s so the copy is visibly its own piece). */
export function duplicateClip(
  video: VideoComposition,
  layerId: string
): { video: VideoComposition; newId: string | null } {
  const clips = clipsOf(video)
  const src = clips.find(c => c.layer.id === layerId)
  if (!src) return { video, newId: null }
  const total = videoTotalDuration(video.beats)
  const len = src.end - src.start
  const start = round2(Math.min(Math.max(0, total - len), src.start + 0.3))
  const newId = newLayerId(src.layer.type)
  const copy: VideoLayerClip = {
    layer: { ...clone(src.layer), id: newId, userEdited: true },
    start,
    end: round2(Math.min(total, start + len)),
  }
  return { video: withClips(video, [...clips, copy]), newId }
}

/** Restack a clip's z relative to the clip above/below it (track order = z order). */
export function moveClipZ(
  video: VideoComposition,
  layerId: string,
  dir: 'up' | 'down'
): VideoComposition {
  const clips = clipsOf(video)
  const ordered = [...clips].sort((a, b) => a.layer.z - b.layer.z)
  const idx = ordered.findIndex(c => c.layer.id === layerId)
  const swapWith = dir === 'up' ? idx + 1 : idx - 1
  if (idx < 0 || swapWith < 0 || swapWith >= ordered.length) return video
  const a = ordered[idx]!
  const b = ordered[swapWith]!
  const za = a.layer.z
  const zb = b.layer.z === za ? za + (dir === 'up' ? 1 : -1) : b.layer.z
  return withClips(
    video,
    clips.map(c =>
      c.layer.id === a.layer.id
        ? { ...c, layer: { ...c.layer, z: zb, userEdited: true } }
        : c.layer.id === b.layer.id
          ? { ...c, layer: { ...c.layer, z: za } }
          : c
    )
  )
}
